import { useEffect, useRef, useState, useCallback } from 'react'
import SectionTitle from '../../../components/SectionTitle/SectionTitle'
import './DiseaseMap.css'
import 'leaflet/dist/leaflet.css'
import { IconClipboardPlus, IconTarget } from '@tabler/icons-react'
import { Button, Center, Divider, Loader, Select } from '@mantine/core'
import axios from 'axios'
import showNotification from '../../../utils/showNotification'
import moment from 'moment'

const LONG_PRESS_MS = 600




function guessCountry(lat, lng, data) {
    if (!data || typeof data !== 'object') return '';
    let closest = null
    let minDist = Infinity
    Object.entries(data).forEach(([country, d]) => {
        const dist = Math.hypot(lat - d.lat, lng - d.lng)
        if (dist < minDist) { minDist = dist; closest = country }
    })
    return minDist < 8 ? closest : ''
}

function isMobile() {
    return window.matchMedia('(pointer: coarse)').matches
}

export default function DiseaseMap() {
    const mapRef = useRef(null)
    const mapInstanceRef = useRef(null)
    const longPressTimer = useRef(null)
    const touchMoved = useRef(false)
    const rippleRef = useRef(null)

    const [selected, setSelected] = useState(null)   // { country, diseases, latlng }
    const [ctxMenu, setCtxMenu] = useState(null)   // { x, y, latlng } — desktop only
    const [modal, setModal] = useState(false)
    const [pendingLatlng, setPendingLatlng] = useState(null)
    const [longPressing, setLongPressing] = useState(false)  // shows ripple feedback
    const [toast, setToast] = useState(false)
    const [reFetch, setReFetch] = useState(false)
    const [errors, setErrors] = useState({})
    const [form, setForm] = useState({
        user_name: '', country: '', species: 'Canino', pathology: '', date: '', other_pathology: ""
    })
    const [DATA, setData] = useState(null)
    const [isLoading, setIsLoading] = useState(false)

    // ── Open modal with a given latlng ──────────────────────────────────────
    const openModalAt = useCallback((latlng, countryOverride = null) => {
        // Pasamos DATA como tercer argumento
        const country = countryOverride ?? guessCountry(latlng?.lat, latlng?.lng, DATA?.data);

        setForm({
            user_name: '',
            country: country.toLowerCase(), // Normalizamos para el select
            species: 'Canino',
            pathology: '',
            date: new Date().toISOString().slice(0, 10),
        });
        setErrors({});
        setPendingLatlng(latlng);
        setCtxMenu(null);
        setModal(true);
    }, [DATA]);

    // ── Open modal from panel button (mobile: country already known) ─────────
    function openModalFromPanel() {
        if (!selected) return
        const latlng = { lat: DATA?.data[selected.country]?.lat ?? 0, lng: DATA?.data[selected.country]?.lng ?? 0 }
        openModalAt(latlng, selected.country)
    }

    const INITIAL_CENTER = [-25, -65]
    const INITIAL_ZOOM = 3

    useEffect(() => {
        const fetchData = async () => {
            try {
                setIsLoading(true)
                const { data } = await axios.get(`${process.env.REACT_APP_BACKEND_SERVER}/info/pathology-reports`);
                setData(data);
            } catch (error) {
                console.error("Error cargando el mapa:", error);
                showNotification({
                    color: "red",
                    status: 'error',
                    title: "Error de conexión",
                    message: "No se pudieron cargar los datos del mapa."
                });
            } finally {
                setIsLoading(false)
            }
        };

        fetchData();
    }, [reFetch]);

    useEffect(() => {
        if (!DATA || mapInstanceRef.current) return


        import('leaflet').then((L) => {
            const map = L.map(mapRef.current, {
                center: [-25, -65],
                zoom: 3,
                zoomControl: false,
                attributionControl: false,
            })

            L.tileLayer('https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png', {
                maxZoom: 18,
            }).addTo(map)

            L.control.zoom({ position: 'bottomright' }).addTo(map)

            // ── Markers ────────────────────────────────────────────────────
            Object.entries(DATA?.data).forEach(([country, d]) => {
                const HAS_DISEASES = d.diseases?.length > 0
                const mainColor = HAS_DISEASES ? d.diseases[0].color : '#adadadff';
                const size = HAS_DISEASES ? 26 : 20
                const html = `
                    <div style="position:relative;width:${size}px;height:${size}px;display:flex;align-items:center;justify-content:center;">
                        <div style="position:absolute;width:${size}px;height:${size}px;border-radius:50%;background:${mainColor};opacity:.2;animation:dm-pulse 2s ease-out infinite;"></div>
                        <div style="width:14px;height:14px;border-radius:50%;background:${mainColor};border:2.5px solid white;z-index:2;box-shadow:0 1px 5px rgba(0,0,0,.25);"></div>
                    </div>`
                const icon = L.divIcon({ html, className: '', iconSize: [size, size], iconAnchor: [size / 2, size / 2] })
                const marker = L.marker([d.lat, d.lng], { icon }).addTo(map)
                marker.on('click', () => {
                    setCtxMenu(null)
                    setSelected({ country, diseases: d.diseases })
                })
            })

            const container = map.getContainer()

            // ── Desktop: right-click ───────────────────────────────────────
            container.addEventListener('contextmenu', (e) => {
                e.preventDefault()
                if (isMobile()) return   // mobile handles via long press
                const latlng = map.containerPointToLatLng([e.offsetX, e.offsetY])
                setCtxMenu({ x: e.offsetX, y: e.offsetY, latlng })
            })

            container.addEventListener('click', () => setCtxMenu(null))

            // ── Mobile: long press ─────────────────────────────────────────
            container.addEventListener('touchstart', (e) => {
                touchMoved.current = false
                const touch = e.touches[0]
                const rect = container.getBoundingClientRect()
                const x = touch.clientX - rect.left
                const y = touch.clientY - rect.top

                // Show ripple at touch point
                if (rippleRef.current) {
                    rippleRef.current.style.left = x + 'px'
                    rippleRef.current.style.top = y + 'px'
                    rippleRef.current.style.display = 'block'
                }
                setLongPressing(true)

                longPressTimer.current = setTimeout(() => {
                    if (touchMoved.current) return
                    const latlng = map.containerPointToLatLng([x, y])
                    setLongPressing(false)
                    if (rippleRef.current) rippleRef.current.style.display = 'none'
                    openModalAt(latlng)
                }, LONG_PRESS_MS)
            }, { passive: true })

            container.addEventListener('touchmove', () => {
                touchMoved.current = true
                clearTimeout(longPressTimer.current)
                setLongPressing(false)
                if (rippleRef.current) rippleRef.current.style.display = 'none'
            }, { passive: true })

            container.addEventListener('touchend', () => {
                clearTimeout(longPressTimer.current)
                setLongPressing(false)
                if (rippleRef.current) rippleRef.current.style.display = 'none'
            }, { passive: true })

            mapInstanceRef.current = map
        })

        return () => {
            clearTimeout(longPressTimer.current)
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove()
                mapInstanceRef.current = null
            }
        }
    }, [DATA?.data, openModalAt])



    function closeModal() { setModal(false) }

    async function submitReport() {
        const errs = {};
        if (!form.user_name.trim()) errs.user_name = true;
        if (!form.country) errs.country = true;
        if (!form.pathology) errs.pathology = true;
        if (Object.keys(errs).length) { setErrors(errs); return; }

        const values = {
            ...form,
            location: pendingLatlng
        };

        try {
            await axios.post(`${process.env.REACT_APP_BACKEND_SERVER}/info/pathology-reports`, values);

            closeModal();
            setToast(true);
            setTimeout(() => setToast(false), 3000);

            setReFetch(!reFetch)

        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "Error al enviar el reporte",
                message: `Por favor, intenta nuevamente más tarde`,
            });
        }
    }

    const totalReports = DATA?.stats?.totalReports
    const totalCountries = DATA?.stats?.totalCountries
    const totalPatho = DATA?.stats?.totalPatho
    const startDate = DATA?.config?.date?.start
    const endDate = DATA?.config?.date?.end

    if (!DATA && !isLoading) return

    return (
        <section id='mapa' className="map-section section-wrapper">
            <SectionTitle upText="Exclusivo FORVET" downText="Mapa de Realidades Clínicas" />

            <div className="map-grid">
                <div className="map-text">
                    <p className="section-desc">
                        El primer <strong>informe colaborativo</strong> de enfermedades de mayor frecuencia por región en
                        <strong> LATAM</strong>. Construido por<strong> veterinarios, para veterinarios.</strong>
                    </p>
                    <div className="map-features">
                        <div className="map-feature">
                            <div className="map-feature-icon">📍</div>
                            <div className="map-feature-text">
                                <h4>Datos por región</h4>
                                <p>Prevalencia real reportada por veterinarios activos en cada zona, no estadísticas de laboratorio.</p>
                            </div>
                        </div>
                        <div className="map-feature">
                            <div className="map-feature-icon">📈</div>
                            <div className="map-feature-text">
                                <h4>Tendencias en tiempo real</h4>
                                <p>Detectá brotes, cambios estacionales y patrones que ninguna publicación académica te va a mostrar a tiempo.</p>
                            </div>
                        </div>
                        <div className="map-feature">
                            <div className="map-feature-icon">🤝</div>
                            <div className="map-feature-text">
                                <h4>Inteligencia colectiva</h4>
                                <p>Cada caso que se reporta enriquece el mapa. Cuantos más somos, más preciso es el diagnóstico regional.</p>
                            </div>
                        </div>
                       <div className='map-feature--actionBtn'>
                         <Divider color='var(--color-text-primary)' w='100%' label='Participá y sé parte' />
                        <div className="map-feature">
                            <Button fullWidth leftSection={<IconClipboardPlus />} onClick={() => openModalAt()} color='var(--color-secondary-darkest)' type='button'>Agregar un nuevo caso</Button>
                        </div>
                       </div>
                    </div>
                </div>

                {isLoading &&
                    <Center><Loader size={60} color='white' /></Center>
                }
                {(DATA && !isLoading) && <div className="map-visual">
                    <div className="dm-map-wrapper">

                        {/* Leaflet map */}
                        <div ref={mapRef} className="dm-map" />

                        {/* Long press ripple feedback */}
                        <div
                            ref={rippleRef}
                            className={`dm-ripple${longPressing ? ' dm-ripple--active' : ''}`}
                        />

                        {/* boton de reestablecer */}
                        <button
                            className="dm-recenter-btn"
                            onClick={() => mapInstanceRef.current?.setView(INITIAL_CENTER, INITIAL_ZOOM)}
                        >
                            <IconTarget size={14} />

                            Centrar mapa
                        </button>

                        {/* Stats bar */}
                        <div className="dm-stats-bar-container">
                            {startDate && endDate && <div className="dm-stat">
                                <span className="dm-stat-lbl dm-stat-date">desde <strong>{moment(startDate).format("DD/MM/YYYY")}</strong> al <strong>{moment(endDate).format('DD/MM/YYYY')}</strong></span>
                            </div>}
                            <div className='dm-stats-bar'>
                                {totalCountries && <div className="dm-stat">
                                    <span className="dm-stat-num">{totalCountries}</span>
                                    <span className="dm-stat-lbl">Países</span>
                                </div>}
                                {totalReports && <div className="dm-stat">
                                    <span className="dm-stat-num">{totalReports}</span>
                                    <span className="dm-stat-lbl">Reportes</span>
                                </div>}
                                {totalPatho && <div className="dm-stat">
                                    <span className="dm-stat-num">{totalPatho}</span>
                                    <span className="dm-stat-lbl">Patologías</span>
                                </div>}
                            </div>
                        </div>

                        {/* Side panel */}
                        <div className={`dm-side-panel${selected ? ' dm-side-panel--open' : ''}`}>
                            <div className="dm-panel-header">
                                <div>
                                    <div className="dm-panel-country">{selected?.country ?? '—'}</div>
                                    <div className="dm-panel-sub">
                                        {selected ? `${selected.diseases.length} patologías registradas` : ''}
                                    </div>
                                </div>
                                {selected && (
                                    <button className="dm-panel-close" onClick={() => setSelected(null)}>✕</button>
                                )}
                            </div>

                            <div className="dm-panel-body">
                                {selected?.diseases.map((dis) => (
                                    <div key={dis.name} className="dm-disease-row">
                                        <div className="dm-disease-top">
                                            <span className="dm-disease-name">{dis.name}</span>
                                            <span className="dm-disease-pct" style={{ color: dis.color }}>{dis.pct}%</span>
                                        </div>
                                        <div className="dm-bar-bg">
                                            <div className="dm-bar-fill" style={{ width: `${dis.pct}%`, background: dis.color }} />
                                        </div>
                                        <div className="dm-disease-obs">{dis.obs}</div>
                                    </div>
                                ))}
                            </div>

                            {/* Panel footer: hint on desktop, report button on mobile */}
                            <div className="dm-panel-footer">
                                {selected && (
                                    <button
                                        className="dm-panel-report-btn dm-panel-report-btn--mobile"
                                        onClick={openModalFromPanel}
                                    >
                                        + Reportar caso en {selected.country}
                                    </button>
                                )}
                                <span className="dm-hint dm-hint--desktop">
                                    Click derecho en el mapa para reportar
                                </span>
                                <span className="dm-hint dm-hint--mobile">
                                    Mantené presionado el mapa para reportar con coordenadas exactas
                                </span>
                            </div>
                        </div>

                        {/* Hint when no country selected */}
                        {!selected && (
                            <div className="dm-click-hint">
                                <span className="dm-hint--desktop">Hacé click en un país</span>
                                <span className="dm-hint--mobile">Tocá un marcador</span>
                            </div>
                        )}

                        {/* Desktop context menu */}
                        {ctxMenu && (
                            <div className="dm-ctx-menu" style={{ left: ctxMenu.x + 4, top: ctxMenu.y + 4 }}>
                                <div className="dm-ctx-item" onClick={() => openModalAt(ctxMenu.latlng)}>
                                    <div className="dm-ctx-icon dm-ctx-icon--green">+</div>
                                    <div>
                                        <div className="dm-ctx-label">Reportar caso aquí</div>
                                        <div className="dm-ctx-sub">
                                            {ctxMenu.latlng.lat.toFixed(2)}°, {ctxMenu.latlng.lng.toFixed(2)}°
                                        </div>
                                    </div>
                                </div>
                                <div className="dm-ctx-sep" />
                                <div className="dm-ctx-item dm-ctx-item--muted" onClick={() => setCtxMenu(null)}>
                                    <div className="dm-ctx-icon dm-ctx-icon--gray">✕</div>
                                    Cancelar
                                </div>
                            </div>
                        )}

                        {/* Toast */}
                        {toast && (
                            <div className="dm-toast">Reporte enviado — gracias por contribuir al mapa</div>
                        )}
                    </div>

                    <p className="map-placeholder-text">
                        <span className="dm-hint--desktop">
                            {selected ? 'Click derecho en el mapa para reportar un caso' : 'Hacé click en un país · Click derecho para reportar'}
                        </span>
                        <span className="dm-hint--mobile">
                            {selected ? 'Tocá "Reportar" o mantené presionado el mapa' : 'Tocá un marcador · Mantené presionado para reportar'}
                        </span>
                    </p>
                </div>}
            </div>

            {/* Report modal */}
            {modal && (
                <div className="dm-overlay" onClick={(e) => e.target === e.currentTarget && closeModal()}>
                    <div className="dm-modal">
                        <div className="dm-modal-head">
                            <div>
                                <div className="dm-modal-title">Reportar caso clínico</div>
                                {pendingLatlng && (
                                    <div className="dm-modal-loc">
                                        {pendingLatlng.lat.toFixed(3)}°, {pendingLatlng.lng.toFixed(3)}°
                                    </div>
                                )}
                            </div>
                            <button className="dm-modal-close" onClick={closeModal}>✕</button>
                        </div>

                        <div className="dm-modal-body">
                            <div className="dm-field">
                                <label>Veterinario</label>
                                <input
                                    type="text"
                                    placeholder="Nombre Apellido"
                                    value={form.user_name}
                                    onChange={(e) => setForm({ ...form, user_name: e.target.value })}
                                    className={errors.user_name ? 'dm-input-error' : ''}
                                />
                            </div>
                            <div className="dm-field">
                                <Select
                                  comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                                    label='País'
                                    searchable
                                    styles={{ input: { textTransform: "capitalize" } }}
                                    error={errors.country ? "Falta el país" : ""}
                                    required
                                    defaultValue={form?.country}
                                    data={DATA?.config?.countries || []}
                                    onChange={value => setForm(prev => ({ ...prev, country: value }))}
                                />
                            </div>
                            <div className="dm-field">
                                <label>Especie</label>
                                <div className="dm-species-row">
                                    {DATA?.config?.species?.map((sp) => (
                                        <div
                                            key={sp}
                                            className={`dm-sp-btn${form.species === sp ? ' dm-sp-btn--active' : ''}`}
                                            onClick={() => setForm({ ...form, species: sp })}
                                        >
                                            {sp}
                                        </div>
                                    ))}
                                </div>
                            </div>
                            <div className="dm-field">
                                <Select
                                comboboxProps={{ withinPortal: true, zIndex: 9999 }}
                                    label='Patología / diagnóstico'
                                    data={DATA?.config?.pathologies || []}
                                    searchable
                                    error={errors.pathology ? "Falta la patología" : ""}
                                    allowDeselect={false}
                                    onChange={(value) => setForm({ ...form, pathology: value })}
                                />

                                {form.pathology?.toLowerCase() === 'otra' &&
                                    <div className="dm-field">
                                        <label>Escribe el nombre de la patología</label>
                                        <input
                                            type="text"
                                            value={form.other_pathology}
                                            onChange={(e) => setForm({ ...form, other_pathology: e.target.value })}
                                            required
                                        />
                                    </div>
                                }
                            </div>
                            <div className="dm-field">
                                <label>Fecha del caso</label>
                                <input
                                    type="date"
                                    value={form.date}
                                    onChange={(e) => setForm({ ...form, date: e.target.value })}
                                />
                            </div>
                        </div>
                        <div className="dm-modal-footer">
                            <button className="dm-btn-cancel" onClick={closeModal}>Cancelar</button>
                            <button className="dm-btn-submit" onClick={submitReport}>Enviar reporte</button>
                        </div>
                    </div>

                </div>
            )}
        </section>
    )
}